import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Bot, Sparkles, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUICK_PROMPTS = {
  it: [
    "Quale eSIM mi consigli per la Spagna?",
    "Come installo una eSIM?",
    "Il mio telefono è compatibile?",
    "Cosa succede se finisco i dati?",
    "Consigli di viaggio per il Giappone",
  ],
  en: [
    "Which eSIM do you recommend for Spain?",
    "How do I install an eSIM?",
    "Is my phone compatible?",
    "What happens when I run out of data?",
    "Travel tips for Japan",
  ],
  pl: [
    "Którą eSIM polecasz do Hiszpanii?",
    "Jak zainstalować eSIM?",
    "Czy mój telefon jest kompatybilny?",
    "Co się stanie, gdy skończą mi się dane?",
    "Porady podróżne do Japonii",
  ],
  fr: [
    "Quelle eSIM recommandez-vous pour l'Espagne?",
    "Comment installer une eSIM?",
    "Mon téléphone est-il compatible?",
    "Que se passe-t-il quand mes données sont épuisées?",
    "Conseils de voyage pour le Japon",
  ],
  es: [
    "¿Qué eSIM recomiendas para España?",
    "¿Cómo instalo una eSIM?",
    "¿Mi teléfono es compatible?",
    "¿Qué pasa cuando se me acaban los datos?",
    "Consejos de viaje para Japón",
  ],
  de: [
    "Welche eSIM empfehlen Sie für Spanien?",
    "Wie installiere ich eine eSIM?",
    "Ist mein Telefon kompatibel?",
    "Was passiert, wenn mein Datenvolumen aufgebraucht ist?",
    "Reisetipps für Japan",
  ],
  ru: [
    "Какую eSIM вы рекомендуете для Испании?",
    "Как установить eSIM?",
    "Мой телефон совместим?",
    "Что происходит, когда заканчиваются данные?",
    "Советы для путешествия в Японию",
  ],
};

const TITLES = {
  it: { title: "Assistente di Viaggio AI", subtitle: "Chiedi qualsiasi cosa", welcome: "Ciao! Sono il tuo assistente di viaggio AI. Posso aiutarti a scegliere la eSIM perfetta, darti consigli di viaggio, e rispondere a qualsiasi domanda. Come posso aiutarti?", placeholder: "Scrivi un messaggio...", thinking: "Sto pensando..." },
  en: { title: "AI Travel Assistant", subtitle: "Ask me anything", welcome: "Hi! I'm your AI travel assistant. I can help you choose the perfect eSIM, give you travel tips, and answer any questions. How can I help?", placeholder: "Type a message...", thinking: "Thinking..." },
  pl: { title: "Asystent Podróży AI", subtitle: "Zapytaj o cokolwiek", welcome: "Cześć! Jestem Twoim asystentem podróży AI. Mogę pomóc Ci wybrać idealną eSIM, dać wskazówki podróżne i odpowiedzieć na pytania. Jak mogę pomóc?", placeholder: "Napisz wiadomość...", thinking: "Myślę..." },
  fr: { title: "Assistant Voyage IA", subtitle: "Demandez-moi n'importe quoi", welcome: "Bonjour! Je suis votre assistant voyage IA. Je peux vous aider à choisir la bonne eSIM, vous donner des conseils et répondre à vos questions. Comment puis-je vous aider?", placeholder: "Écrivez un message...", thinking: "Je réfléchis..." },
  es: { title: "Asistente de Viaje IA", subtitle: "Pregúntame lo que sea", welcome: "¡Hola! Soy tu asistente de viaje IA. Puedo ayudarte a elegir la eSIM perfecta, darte consejos de viaje y responder tus preguntas. ¿Cómo puedo ayudarte?", placeholder: "Escribe un mensaje...", thinking: "Pensando..." },
  de: { title: "KI-Reiseassistent", subtitle: "Fragen Sie mich alles", welcome: "Hallo! Ich bin Ihr KI-Reiseassistent. Ich kann Ihnen bei der Auswahl der perfekten eSIM helfen, Reisetipps geben und Fragen beantworten. Wie kann ich helfen?", placeholder: "Nachricht schreiben...", thinking: "Ich denke nach..." },
  ru: { title: "ИИ Помощник", subtitle: "Спросите о чём угодно", welcome: "Привет! Я ваш ИИ-ассистент по путешествиям. Могу помочь выбрать идеальную eSIM, дать советы и ответить на вопросы. Чем могу помочь?", placeholder: "Введите сообщение...", thinking: "Думаю..." },
};

function MessageBubble({ text, isBot }) {
  const formatted = text.split('\n').map((line, i) => {
    let processed = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
    if (line.startsWith('- ') || line.startsWith('• ')) {
      processed = '<span class="text-teal-400 mr-1.5">•</span>' + processed.slice(2);
    }
    if (/^\d+\.\s/.test(line)) {
      const num = line.match(/^(\d+)\./)[1];
      processed = `<span class="text-teal-400 font-mono mr-1.5">${num}.</span>` + line.replace(/^\d+\.\s/, '');
    }
    return <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: processed || '&nbsp;' }} />;
  });

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
      className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-3`}>
      {isBot && (
        <div className="w-7 h-7 rounded-full bg-teal-500/20 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-teal-400" />
        </div>
      )}
      <div className={`max-w-[82%] rounded-2xl px-4 py-3 ${
        isBot 
          ? 'bg-white/[0.06] text-slate-300 rounded-bl-md border border-white/[0.06]' 
          : 'bg-teal-500/20 text-teal-50 rounded-br-md border border-teal-500/10'
      }`}>
        {formatted}
      </div>
    </motion.div>
  );
}

function TypingIndicator() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start mb-3">
      <div className="w-7 h-7 rounded-full bg-teal-500/20 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
        <Sparkles className="w-3.5 h-3.5 text-teal-400" />
      </div>
      <div className="bg-white/[0.06] border border-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5 items-center">
        <span className="w-2 h-2 bg-teal-400/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-teal-400/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-teal-400/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </motion.div>
  );
}

export default function ChatBot() {
  const { i18n } = useTranslation();
  const lang = TITLES[i18n.language] ? i18n.language : 'en';
  const t = TITLES[lang];
  const prompts = QUICK_PROMPTS[lang] || QUICK_PROMPTS.en;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showQuickPrompts, setShowQuickPrompts] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize session from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('esim_chat_session');
    if (stored) setSessionId(stored);
  }, []);

  // Welcome message when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ text: t.welcome, isBot: true }]);
    }
  }, [isOpen, messages.length, t.welcome]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;
    
    setMessages(prev => [...prev, { text, isBot: false }]);
    setInputText('');
    setIsLoading(true);
    setShowQuickPrompts(false);

    try {
      const res = await fetch(`${API}/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const data = await res.json();
      
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('esim_chat_session', data.session_id);
      }
      
      setMessages(prev => [...prev, { text: data.response, isBot: true }]);
    } catch {
      setMessages(prev => [...prev, { text: "Connection error. Please try again.", isBot: true }]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading]);

  const handleReset = () => {
    setMessages([{ text: t.welcome, isBot: true }]);
    setShowQuickPrompts(true);
    setSessionId(null);
    localStorage.removeItem('esim_chat_session');
    setInputText('');
  };

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <motion.button initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-[150] w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white rounded-full shadow-lg shadow-teal-500/30 flex items-center justify-center transition-all duration-200 hover:shadow-teal-500/50 hover:scale-105"
            data-testid="chat-bubble">
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[150] w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-5rem)] bg-[#0c1117] border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
            data-testid="chat-window">
            
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600/90 to-teal-500/90 backdrop-blur-sm px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm leading-tight">{t.title}</h3>
                  <p className="text-white/60 text-xs">{t.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleReset} className="text-white/50 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Reset" data-testid="chat-reset">
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button onClick={() => setIsOpen(false)} className="text-white/50 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="chat-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="chat-messages">
              {messages.map((msg, i) => (
                <MessageBubble key={i} text={msg.text} isBot={msg.isBot} />
              ))}
              {isLoading && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick prompts */}
            {showQuickPrompts && messages.length <= 1 && (
              <div className="px-3 pb-2 flex-shrink-0">
                <div className="flex flex-wrap gap-1.5">
                  {prompts.map((prompt, i) => (
                    <button key={i} onClick={() => sendMessage(prompt)}
                      className="text-xs px-3 py-1.5 bg-white/[0.05] hover:bg-teal-500/15 border border-white/[0.08] hover:border-teal-500/30 rounded-full text-slate-400 hover:text-teal-300 transition-all duration-150"
                      data-testid={`chat-quick-${i}`}>
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-white/[0.06] p-3 flex-shrink-0 bg-[#0a0e13]">
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(inputText); }} className="flex gap-2">
                <input ref={inputRef} type="text" value={inputText} onChange={(e) => setInputText(e.target.value)}
                  placeholder={t.placeholder}
                  disabled={isLoading}
                  className="flex-1 bg-white/[0.05] border border-white/[0.08] rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-teal-500/40 focus:bg-white/[0.07] transition-all disabled:opacity-50"
                  data-testid="chat-input" />
                <button type="submit" disabled={isLoading || !inputText.trim()}
                  className="bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:text-slate-500 text-black font-semibold px-3.5 py-2.5 rounded-xl text-sm transition-all duration-150 disabled:cursor-not-allowed"
                  data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
